@let isLoadingUpload = isLoadingDocumentDialog$ | async;
@let editingId = isLoadingEditDialog$ | async;
@let isDeleting = isDeleting$ | async;
<mat-expansion-panel
  class="info-panel remove-mat-expansion-panel-padding"
  (opened)="panelOpenState = true"
  (closed)="panelOpenState = false"
  [expanded]="panelOpenState">
  <mat-expansion-panel-header #sectionHeader>
    <mat-panel-title>
      <div class="section-header-container">
        <p class="overline grey1 semibold section-title">{{sysText.attachedDocuments | uppercase}}</p>
        <mat-divider class="section-header-underline" aria-hidden="true"></mat-divider>
      </div>
    </mat-panel-title>
  </mat-expansion-panel-header>

  <div class="main-content">
    @if (templateDetails.canEditDocuments) {
      <div class="upload-button-container">
        <!-- upload keeps focus while its dialog loads: showProgress lays the spinner over its label, disabledInteractive keeps
        it focusable, and the guard blocks clicks -->
        <button
          mat-flat-button
          color="accent"
          class="button"
          [disabled]="isLoadingUpload"
          disabledInteractive
          [showProgress]="isLoadingUpload"
          [attr.aria-label]="isLoadingUpload ? sysText.loading_ariaLabel : null"
          (click)="!isLoadingUpload && uploadDocument()">
          {{sysText.uploadDocument}}
          <mat-spinner progressIndicator diameter="30" color="accent"></mat-spinner>
        </button>
      </div>
    }

    @if (documents == null || documents?.length == 0) {
      <p class="body2 grey2 no-documents">{{ sysText.noDocuments }}</p>
    }

    @if (documents?.length > 0) {
      <div [ngClass]="{ 'make-scroll': !(isMobile$ | async) }" class="container">
        <table mat-table [dataSource]="_docs" [trackBy]="trackByDocId" id="data-table">
          <!-- Document Title Column -->
          <ng-container matColumnDef="documentTitle">
            <th mat-header-cell *matHeaderCellDef>
              <span>{{ sysText.documentTitle | titlecase }}</span>
            </th>
            <td mat-cell *matCellDef="let doc" [ngClass]="{ smaller: (isMobile$ | async) }">
              @let isDownloading = isDownloadingDocument$ | async;
              <!-- while downloading the title stays focusable (aria-disabled) and the guard blocks clicks; download-link keeps
              the spinner beside it -->
              <span class="download-link">
                <button
                  type="button"
                  class="button-reset click-link"
                  [attr.aria-disabled]="isDownloading || null"
                  (click)="!isDownloading && downloadDocument(doc.templateDocumentId)">
                  {{ doc.documentTitle }} ({{doc.fileExtension}})
                </button>
                @if (isDownloading && isDownloadingDocument(doc.templateDocumentId)) {
                  <mat-spinner class="download-spinner" [diameter]="18" color="accent"></mat-spinner>
                }
              </span>
            </td>
          </ng-container>
          <!-- Document Category Column -->
          <ng-container matColumnDef="documentCategory">
            <th mat-header-cell *matHeaderCellDef>
              <span>{{ sysText.documentCategory | titlecase }}</span>
            </th>
            <td mat-cell *matCellDef="let doc" [ngClass]="{ smaller: (isMobile$ | async) }">
              <span>{{ doc.documentCategory }}</span>
            </td>
          </ng-container>
          <!-- Comments Column -->
          <ng-container matColumnDef="comments">
            <th mat-header-cell *matHeaderCellDef>
              <span>{{ sysText.documentComments | titlecase }}</span>
            </th>
            <td
              class="comments"
              mat-cell
              *matCellDef="let doc"
              [ngClass]="{ smaller: (isMobile$ | async) }">
              <span>{{ doc.documentComments == null ? "--" : doc.documentComments }}</span>
            </td>
          </ng-container>
          <!-- Date Added Column -->
          <ng-container matColumnDef="dateAdded">
            <th mat-header-cell *matHeaderCellDef>
              <span>{{ sysText.dateAdded | titlecase }}</span>
            </th>
            <td mat-cell *matCellDef="let doc" [ngClass]="{ smaller: (isMobile$ | async), 'mat-cell-no-bottom': (doc.documentComments != null) }">
              <span>{{ doc.createDateTime | date: 'MMM d, y h:mm a' }}</span>
            </td>
          </ng-container>
          <!-- Uploaded By Column -->
          <ng-container matColumnDef="uploadedBy">
            <th mat-header-cell *matHeaderCellDef>
              <span>{{ sysText.uploadedBy | titlecase }}</span>
            </th>
            <td mat-cell *matCellDef="let doc" [ngClass]="{ smaller: (isMobile$ | async) }" >
              @if (doc.uploadedBy) {
                <app-avatar
                  [name]="doc.uploadedBy.description"
                  [image]="doc.uploadedBy.image">
                </app-avatar>
              }
            </td>
          </ng-container>
          <!--Icons Column -->
          <ng-container matColumnDef="icons" stickyEnd>
            <th mat-header-cell *matHeaderCellDef>
            </th>
            <td mat-cell *matCellDef="let doc" [ngClass]="{ smaller: (isMobile$ | async) }" class="mat-cell-action-buttons">
              @if (templateDetails.canEditDocuments) {
                <!-- the icons stay while busy: the pressed one keeps focus with the spinner over its icon (show-progress) and its
                guard blocks clicks, the rest grey out -->
                @let busy = editingId != null || isDeleting;
                @let deleting = isDeleting && isSubmittingId === doc.templateDocumentId;
                @let editing = editingId === doc.templateDocumentId;
                <div class="icons">
                  <button
                    type="button"
                    class="button-reset"
                    [class.show-progress]="deleting"
                    [disabled]="busy && !deleting"
                    [attr.aria-label]="deleting ? sysText.submitting_ariaLabel : sysText.delete_ariaLabel"
                    (click)="!busy && deleteDocument(doc.templateDocumentId)">
                    <mat-icon>delete</mat-icon>
                    @if (deleting) {
                      <mat-spinner diameter="20" color="accent"></mat-spinner>
                    }
                  </button>
                  <button
                    type="button"
                    class="button-reset"
                    [class.show-progress]="editing"
                    [disabled]="busy && !editing"
                    [attr.aria-label]="editing ? sysText.loading_ariaLabel : sysText.edit_ariaLabel"
                    (click)="!busy && editDocument(doc.templateDocumentId)">
                    <mat-icon>edit</mat-icon>
                    @if (editing) {
                      <mat-spinner diameter="20" color="accent"></mat-spinner>
                    }
                  </button>
                </div>
              }
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
        </table>
      </div>
    }
  </div>
</mat-expansion-panel>